import { FC, useEffect, useState } from "react";
import styles from "./LinkedDealers.module.scss";
import { Button, Title, UserAvatar } from "@/shared";
import { useDrawer } from "@/app/providers/DrawerProvider/useDrawer";
import CrossIcon from "@/shared/assets/crossIcon.svg";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import {
  changeManagerDealers,
  fetchUsers,
  setUserForReview,
} from "@/entities/users/slice";
import type { IUser } from "@/entities/users/types";
import { Select, Tooltip } from "antd";
import { UserInfo } from "@/widgets";
import ArrowLeft from "@/shared/assets/leftMiniArrow.svg";
import PlusIcon from "@/shared/assets/whitePlusIcon.svg";
import { fetchDealers } from "@/entities/dealer";

interface LinkedDealersProps {}

const dealerDisplayLabel = (name: string, shortName?: string | null) =>
  shortName?.trim() || name;

const LinkedDealers: FC<LinkedDealersProps> = () => {
  const { closeDrawer } = useDrawer();
  const { dealersList: allDealerList } = useAppSelector(
    (state) => state.dealers,
  );
  const { userForReview } = useAppSelector((state) => state.users);
  const [showAdder, setShowAdder] = useState<boolean>(false);
  const [dealersIds, setDealersIds] = useState<string[]>(
    userForReview?.managerDealers?.map((i) => i.dealerId) ?? [],
  );

  const dispatch = useAppDispatch();
  const dealersList = userForReview?.managerDealers ?? [];
  const preparedDealerList = allDealerList
    .filter((mainShop) => mainShop.mainINN === null)
    .map((item) => ({
      value: item.id,
      label: dealerDisplayLabel(item.name, item.shortName),
    }));
  const { openDrawer } = useDrawer();

  const handleReturn = () => {
    openDrawer(<UserInfo />, { maxWidth: 475 });
  };

  const handleRemove = async (dealerId: string) => {
    if (!userForReview?.id) return;
    const body = {
      userId: userForReview.id,
      dealerIds: (userForReview?.managerDealers ?? [])
        .filter((item) => item.dealerId !== dealerId)
        .map((item) => item.dealerId),
    };
    await dispatch(changeManagerDealers(body));
  };

  const handleSelect = (values: string[]) => {
    setDealersIds(values);
  };

  const handleSave = async () => {
    if (!userForReview?.id) return;
    const body = {
      userId: userForReview.id,
      dealerIds: dealersIds,
    };
    await dispatch(changeManagerDealers(body));
    await dispatch(fetchUsers(""));
    // Обновляем userForReview из обновлённого списка пользователей
    const updatedUsersResult = await dispatch(fetchUsers(""));
    const updatedUser = (
      updatedUsersResult.payload as { items?: IUser[] } | undefined
    )?.items?.find((u) => u.id === userForReview.id);
    if (updatedUser) {
      dispatch(setUserForReview(updatedUser));
    }
    setShowAdder(false);
  };

  useEffect(() => {
    if (showAdder) dispatch(fetchDealers({ filters: [] }));
  }, [showAdder]);

  useEffect(() => {
    setDealersIds(
      userForReview?.managerDealers?.map((item) => item.dealerId) ?? [],
    );
    dispatch(fetchUsers(""));
  }, [userForReview]);

  return (
    <div className={styles.historyWrapper}>
      <div className={styles.header}>
        <ArrowLeft className={styles.arrow} onClick={handleReturn} />
        <Title text="Привязанные дилеры" type="default" />
        <div className={styles.count}>
          {userForReview?.managerDealers?.length ?? 0} дилеров
        </div>
      </div>

      <div className={styles.title}>
        <UserAvatar
          fio={userForReview?.fio}
          image={userForReview?.image}
          small
        />
        {userForReview?.fio}
      </div>
      <div className={styles.info}>
        <div className={styles.tagsPool}>
          {dealersList.length > 0 ? (
            dealersList.map((dealer) => (
              <div key={dealer.dealerId} className={styles.tag}>
                <span className={styles.tagText}>
                  {dealerDisplayLabel(dealer.name, dealer.shortName)}
                </span>
                <button
                  type="button"
                  className={styles.removeButton}
                  aria-label="Удалить из привязанных"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(dealer.dealerId);
                  }}
                >
                  <CrossIcon className={styles.removeIcon} />
                </button>
              </div>
            ))
          ) : (
            <div className={styles.noInfo}>Нет привязаных дилеров</div>
          )}
        </div>
        <div className={styles.addDealer}>
          {!showAdder ? (
            <Button
              buttontStyle="default"
              name=""
              tooltipTitle={"Привязать нового дилера"}
              onClick={() => setShowAdder(true)}
            >
              <PlusIcon className={styles.plusIcon} />
            </Button>
          ) : (
            <div className={styles.containerSelect}>
              <div className={styles.label}>Привязать дилера: </div>
              <Select
                mode="multiple"
                placeholder="Найдите дилера по названию"
                rootClassName={styles.select}
                options={preparedDealerList}
                value={dealersIds}
                maxTagCount={0}
                maxTagPlaceholder={(omittedValues) => {
                  const n = omittedValues.length;
                  if (n === 0) return null;
                  const listTitle = omittedValues
                    .map(({ label }) => label)
                    .join(", ");
                  return (
                    <Tooltip title={listTitle}>
                      <span className={styles.selectedSummary}>
                        Выбрано {n} дилеров
                      </span>
                    </Tooltip>
                  );
                }}
                filterOption={(input, option) => {
                  const dealer = allDealerList.find(
                    (d) => d.id === option?.value,
                  );
                  if (!dealer) return false;
                  const q = input.toLowerCase();
                  return (
                    dealer.name.toLowerCase().includes(q) ||
                    (dealer.shortName?.toLowerCase().includes(q) ?? false)
                  );
                }}
                onChange={handleSelect}
              />
            </div>
          )}
        </div>
        <div className={styles.actionButtons}>
          <Button
            buttontStyle="cancelBtn"
            name="Закрыть"
            onClick={closeDrawer}
          />
          <Button
            buttontStyle="default"
            name="Сохранить"
            disabled={!showAdder || dealersIds.length === 0}
            onClick={handleSave}
          />
        </div>
      </div>
    </div>
  );
};

export default LinkedDealers;
